import { AccountCircle, Menu, PowerSettingsNew } from '@mui/icons-material';
import {
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import { FC, useCallback, useState } from 'react';
import { AppIcon } from './AppIcon';
import { getAppLinks, getMenuHref, toolsLinks } from './config';
import { useStyles } from './styles';
import { AppName, AppUrls, BoneLink } from './types';

export interface AppDrawerMenuProps {
  appName: AppName;
  topSection?: BoneLink[];
  disconnectUser?: () => void;
  overriddenAppUrls?: Partial<AppUrls>;
}

export const AppDrawerMenu: FC<AppDrawerMenuProps> = ({
  appName,
  topSection,
  disconnectUser,
  overriddenAppUrls,
}) => {
  const classes = useStyles();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = useCallback(
    () => setDrawerOpen((state) => !state),
    [setDrawerOpen],
  );
  const theme = useTheme();
  const bigDisplay = useMediaQuery(theme.breakpoints.up('md'));
  const appIsOneWeb = [AppName.Web, AppName.Diapo, AppName.Video].includes(
    appName,
  );

  const bottomSection: BoneLink[] = [
    {
      href: getMenuHref(AppName.Authors, overriddenAppUrls),
      icon: <AccountCircle />,
      label: 'Mes auteurs',
      openBlank: !appIsOneWeb,
      selected: location.hash === '#/authors/list',
    },
    ...(disconnectUser
      ? [
          {
            label: 'Déconnexion',
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onClick: disconnectUser!,
            icon: <PowerSettingsNew />,
          },
        ]
      : []),
  ];

  const sections = [
    ...(topSection ? [topSection] : []),
    getAppLinks(appName, overriddenAppUrls),
    toolsLinks,
    bottomSection,
  ];

  return (
    <>
      <IconButton
        data-testid="toggle-drawer-button"
        onClick={toggleDrawer}
        title={'Menu'}
        size="large"
      >
        <Menu style={{ color: '#FFF' }} />
      </IconButton>
      {bigDisplay && (
        <>
          <Typography
            className={clsx(classes.title, classes.appBarTitle)}
            variant="h6"
          >
            <AppIcon appName={appName} white className={classes.icon} />
            <span>
              <b>One </b>
              {appName}
            </span>
          </Typography>
        </>
      )}
      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <div data-testid="drawer-menu" className={classes.drawerMenu}>
          <Typography className={classes.title} variant="h5">
            <AppIcon appName={appName} className={classes.icon} />
            <span>
              <b>One </b>
              {appName}
            </span>
          </Typography>

          {sections.map((section, i) => (
            <div key={`section-${i}`}>
              <List>
                {section.map(
                  (
                    {
                      disabled,
                      href,
                      label,
                      icon,
                      openBlank,
                      tooltip,
                      onClick,
                      selected,
                    },
                    j,
                  ) => (
                    <Link
                      key={`link-${j}`}
                      title={tooltip}
                      underline="none"
                      color="inherit"
                      href={!disabled ? href : undefined}
                      onClick={onClick}
                      target={openBlank ? '_blank' : undefined}
                    >
                      <ListItem
                        key={`link-${j}`}
                        button
                        disabled={disabled}
                        selected={selected}
                      >
                        <ListItemIcon>{icon}</ListItemIcon>
                        <ListItemText primary={label} />
                      </ListItem>
                    </Link>
                  ),
                )}
              </List>
              {i < sections.length - 1 && <Divider />}
            </div>
          ))}
        </div>
      </Drawer>
    </>
  );
};
